import axios from 'axios';
import authService from './authService';
import { API_URL } from '@/config/api';

const api = axios.create({
  baseURL: API_URL,
  timeout: 10000,
});

api.interceptors.request.use(
    async (config) => {
      try {
        await authService.ensureAuthentication();

        const authHeaders = authService.getAuthHeaders();

        config.headers = {
          ...config.headers,
          ...authHeaders
        };

        console.log('Headers da requisição:', config.headers);
        return config;
      } catch (error) {
        console.error('Erro no interceptor de requisição:', error);
        return Promise.reject(error);
      }
    },
    (error) => {
      console.error('Erro na preparação da requisição:', error);
      return Promise.reject(error);
    }
);

api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response) {
        console.error('Resposta de erro:', error.response.status, error.response.data);

        if (error.response.status === 401) {
          console.log('Token expirado ou inválido, redirecionando para login...');
          authService.logout();
        }
      } else if (error.request) {
        console.error('Erro na requisição (possível CORS):', error.request);
      } else {
        console.error('Erro geral da API:', error.message);
      }
      return Promise.reject(error);
    }
);

class ApiService {
  constructor(baseEndpoint) {
    this.baseEndpoint = baseEndpoint;
  }

  async get(endpoint = '', params = {}) {
    try {
      const response = await api.get(`${this.baseEndpoint}${endpoint}`, { params });
      return response.data;
    } catch (error) {
      console.error(`Erro ao buscar dados de ${this.baseEndpoint}${endpoint}:`, error);
      throw error;
    }
  }

  async post(endpoint, data) {
    try {
      const response = await api.post(`${this.baseEndpoint}${endpoint}`, data);
      return response.data;
    } catch (error) {
      console.error(`Erro ao criar registro em ${this.baseEndpoint}${endpoint}:`, error);
      throw error;
    }
  }

  async put(endpoint, data) {
    try {
      const response = await api.put(`${this.baseEndpoint}${endpoint}`, data);
      return response.data;
    } catch (error) {
      console.error(`Erro ao atualizar registro em ${this.baseEndpoint}${endpoint}:`, error);
      throw error;
    }
  }

  async delete(endpoint) {
    try {
      const response = await api.delete(`${this.baseEndpoint}${endpoint}`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao excluir registro em ${this.baseEndpoint}${endpoint}:`, error);
      throw error;
    }
  }
}

class ClienteService extends ApiService {
  constructor() {
    super('/clientes');
  }

  async getClientes() {
    try {
      return await this.get();
    } catch (error) {
      console.error('Erro ao buscar todos os clientes:', error);
      return [];
    }
  }

  async buscarClientes(termoBusca) {
    try {
      return await this.get('/buscar', { busca: termoBusca });
    } catch (error) {
      console.error('Erro ao buscar clientes por termo:', error);
      return [];
    }
  }

  async getClientePorId(id) {
    try {
      return await this.get(`/${id}`);
    } catch (error) {
      console.error(`Erro ao buscar cliente ${id}:`, error);
      return null;
    }
  }

  async criarCliente(cliente) {
    try {
      return await this.post('/criar', cliente);
    } catch (error) {
      console.error('Erro ao criar cliente:', error);
      return null;
    }
  }

  async atualizarCliente(id, cliente) {
    try {
      return await this.put(`/${id}`, cliente);
    } catch (error) {
      console.error(`Erro ao atualizar cliente ${id}:`, error);
      return null;
    }
  }

  async excluirCliente(id) {
    try {
      return await this.delete(`/${id}`);
    } catch (error) {
      console.error(`Erro ao excluir cliente ${id}:`, error);
      return false;
    }
  }
}

const clienteServiceInstance = new ClienteService();

const clienteService = {
  getClientes: () => clienteServiceInstance.getClientes(),
  buscarClientes: (termo) => clienteServiceInstance.buscarClientes(termo),
  getClientePorId: (id) => clienteServiceInstance.getClientePorId(id),
  criarCliente: (cliente) => clienteServiceInstance.criarCliente(cliente),
  atualizarCliente: (id, cliente) => clienteServiceInstance.atualizarCliente(id, cliente),
  excluirCliente: (id) => clienteServiceInstance.excluirCliente(id)
};

export default clienteService;